import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { isEmpty } from 'lodash-es';
import { NgScrollbar } from 'ngx-scrollbar';

import { LiveAlertMessage, ShowcaseConstants } from '../showcase.model';
import { ShowcaseTrackingService } from '../showcase.tracking.service';
import { LiveAlertManager } from './live-alert-manager.service';

@Component({
    selector: 'ms-live-alert-list',
    templateUrl: 'live-alert-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LiveAlertListComponent implements OnChanges, OnDestroy {
    animationAlert: LiveAlertMessage;
    lastAlert: LiveAlertMessage | undefined;
    expanded: boolean;
    animate = false;
    alerts: LiveAlertMessage[] = [];
    private startInterval = false;
    @ViewChild(NgScrollbar) alertList: NgScrollbar;
    @HostBinding('class') className = 'alert-message-list';
    @HostBinding('class.alerts-multiple') hasAlertsForExpand = false;
    @HostBinding('class.alerts-none') noAlerts = true;
    private alertsInterval?: () => void;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private timerService: TimerService,
        private showcaseTrackingService: ShowcaseTrackingService,
        private liveAlertManager: LiveAlertManager,
    ) {
        this.setHasAlertsForExpand();
        this.liveAlertManager.alertsEmptied.pipe(takeUntilDestroyed()).subscribe(() => this.setHasAlertsForExpand());
        this.liveAlertManager.newAlert.pipe(takeUntilDestroyed()).subscribe((newAlert) => {
            if (newAlert && !this.liveAlertManager.alerts.some((alert) => alert.eventId === newAlert.eventId && alert.order === newAlert.order)) {
                this.processNewAlert(newAlert);
            } else {
                this.animate = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.stopInterval();
    }

    private stopInterval = () => {
        if (this.alertsInterval) {
            this.startInterval = false;
            this.alertsInterval();
        }
    };

    private displayNewAlert(newAlert: LiveAlertMessage | undefined): void {
        if (newAlert) {
            this.animate = true;
            this.animationAlert = newAlert;
            this.changeDetector.markForCheck();
            this.timerService.setTimeout(() => {
                this.animate = false;
                this.liveAlertManager.alerts.unshift(newAlert);
                this.liveAlertManager.queuedAlerts.shift();
                this.setHasAlertsForExpand();
                if (!this.liveAlertManager.queuedAlerts.length) {
                    this.stopInterval();
                } else if (!this.startInterval && this.liveAlertManager.queuedAlerts.length) {
                    this.createInterval();
                }
            }, ShowcaseConstants.ANIMATIONTIME);
        }
    }

    private createInterval(): void {
        this.startInterval = true;
        this.alertsInterval = this.timerService.setInterval(() => {
            this.displayNewAlert(this.liveAlertManager.queuedAlerts[0]);
        }, ShowcaseConstants.TIMEDELAY);
    }

    private processNewAlert(newAlert: LiveAlertMessage): void {
        if (!this.animate && !this.startInterval) {
            this.displayNewAlert(newAlert);
        } else if (!this.startInterval && this.liveAlertManager.queuedAlerts.length) {
            this.createInterval();
        } else if (!this.liveAlertManager.queuedAlerts.length) {
            this.stopInterval();
        }
    }

    ngOnChanges(changes: ISimpleChanges<LiveAlertListComponent>): void {
        if (this.alertList) {
            this.alertList.update();
        }
    }

    toggleExpand(event: Event): void {
        if (!this.hasAlertsForExpand) {
            return;
        }

        this.expanded = !this.expanded;
        this.showcaseTrackingService.trackExpandGameAlerts(this.expanded);
        event.stopPropagation();
    }

    setHasAlertsForExpand(): void {
        this.alerts = this.liveAlertManager.alerts;
        this.lastAlert = undefined;
        this.noAlerts = true;
        this.hasAlertsForExpand = false;
        if (!isEmpty(this.alerts)) {
            this.noAlerts = false;
            this.lastAlert = this.alerts[0];
            this.hasAlertsForExpand = this.alerts.length > 1;
        }
        this.changeDetector.markForCheck();
    }
}
